import React, { Suspense, lazy } from "react";
import "./styles/ProductDetailsV2.css";
import { useProductDetailsV2 } from "./hooks/useProductDetailsV2";
import { useListingTypeConfig } from "./hooks/useListingTypeConfig";
import { useBlockedUsers } from "../../../shared/contexts/BlockedUsersContext";
import BlockedUserScreen from "../../../shared/BlockedUserScreen/BlockedUserScreen";
import GallerySection from "./childComponents/GallerySection/GallerySection";
import ProductMediaSection from "./childComponents/MediaSection/ProductMediaSection";
import ProductInfoSection from "./childComponents/ProductInfoSection/ProductInfoSection";
import CtaSection from "./childComponents/CtaSection/CtaSection";
import { isInfoListingType } from "../../../../services/helpers/InfoListingHelper";
import SlotCalendarPanelHost from "./childComponents/SlotAvailabilitySection/SlotCalendarPanelHost";

// Info listings get their own page (hero, sponsor, blocks); other types never download it.
const InfoDetailsPage = lazy(() => import("./childComponents/InfoSection/InfoDetailsPage"));

const ProductDetailsV2: React.FC = () => {
  const { product, loading, error } = useProductDetailsV2();
  const { listingTypeConfig } = useListingTypeConfig(product?.listingType);
  const { isBlocked } = useBlockedUsers();

  const goBack = () =>
    window.history.length > 1
      ? window.history.back()
      : (window.location.href = "/home");

  if (loading && !product) {
    return (
      <div className="pdv2-page">
        <div className="pdv2-loading">
          <div className="pdv2-spinner" />
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (product?.sellerId && isBlocked(product.sellerId)) {
    return (
      <div className="pdv2-page">
        <BlockedUserScreen onGoBack={goBack} />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pdv2-page">
        <div className="pdv2-unavailable">
          <p>This product is no longer available. It may have been sold or removed by the seller.</p>
          <button className="pdv2-back-btn" onClick={goBack}>Go Back</button>
        </div>
      </div>
    );
  }

  const ltMod = listingTypeConfig?.id ?? "default";

  if (ltMod === "info" || isInfoListingType(product.listingType)) {
    return (
      <div className="pdv2-page pdv2-page--info">
        <Suspense fallback={<div className="pdv2-loading"><div className="pdv2-spinner" /></div>}>
          <InfoDetailsPage />
        </Suspense>
      </div>
    );
  }

  return (
    <div className={`pdv2-page pdv2-page--${ltMod}`}>
      <GallerySection />
      <ProductMediaSection product={product} />
      <ProductInfoSection />
      <CtaSection />
      {/* Owner's availability Setup/Manage - full-screen panel, same route */}
      <SlotCalendarPanelHost />
    </div>
  );
};

export default ProductDetailsV2;
